import React, { useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { TCSelect } from "components/TopCourtComponents/Input/TopCourtInput";

/**
 * Props:
 * - participants: [{ value, label }]
 * - seedSlots: [{ _id: string, seedNumber: number, participants?: string | {_id, name|label}? }]
 * - size: number (power of two: 4, 8, 16, ...)
 * - onAssign?: ({ slotId, option }) => void
 */
export default function SingleElimBracketAssignById({
  participants,
  seedSlots,
  size,
  onAssign,
  disabled
}) {
  if (!Number.isInteger(Math.log2(size))) {
    throw new Error(`size must be a power of two. got ${size}`);
  }
  if (seedSlots.length !== size) {
    throw new Error(`Expected ${size} seedSlots, got ${seedSlots.length}`);
  }

  // order slots by seedNumber ascending (1..size)
  const ordered = useMemo(
    () => [...seedSlots].sort((a, b) => a.seedNumber - b.seedNumber),
    [seedSlots]
  );

  // initial selections keyed by slotId
  const initialById = useMemo(() => {
    const m = {};
    const toOpt = (sp) => {
      if (!sp) return null;
      if (typeof sp === "string") {
        return participants.find((o) => o.value === sp) || null;
      }
      if (sp && typeof sp === "object" && sp._id) {
        return (
          participants.find((o) => o.value === sp._id) || {
            value: sp._id,
            label: sp.name || sp.label || "Selected",
          }
        );
      }
      return null;
    };
    ordered.forEach((slot) => (m[slot._id] = toOpt(slot.participants)));
    return m;
  }, [ordered, participants]);

  const [selections, setSelections] = useState(initialById);

  // keep in sync if seedSlots change
  useEffect(() => setSelections(initialById), [initialById]);

  // global duplicate prevention (across all slotIds)
  const chosen = useMemo(() => {
    const s = new Set();
    Object.values(selections).forEach((opt) => opt && s.add(opt.value));
    return s;
  }, [selections]);

  const optionsFor = (current) => {
    const curr = current?.value;
    return participants.filter((o) => !chosen.has(o.value) || o.value === curr);
  };

  const handleAssign = (slotId, option) => {
    setSelections((prev) => ({ ...prev, [slotId]: option || null }));
    onAssign && onAssign({ slotId, option: option || null });
  };

  const totalRounds = Math.log2(size);
  const S = styles;

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        {/* <strong>Bracket</strong>
        <span style={S.subtitle}>
          Single Elimination • {size} entrants • {totalRounds} rounds
        </span> */}
      </div>

      <div style={S.scroller}>
        <div
          style={{ ...S.grid, gridTemplateColumns: `repeat(${totalRounds}, 320px)` }}
        >
          {/* Round 1 (editable): pairs (1vs2, 3vs4, ...) */}
          <div style={S.roundCol}>
            <div style={S.roundHeader}>Bracket Seed</div>
            <div style={{ ...S.roundStack, rowGap: 16 }}>
              {Array.from({ length: size / 2 }, (_, i) => {
                const a = ordered[i * 2];
                const b = ordered[i * 2 + 1];
                return (
                  <div key={`r0-m${i}`} style={S.matchBox}>
                    <div style={S.matchInner}>
                      <div style={S.slotRow}>
                        <span style={S.seedTag}>{a.seedNumber}</span>
                        <div style={S.selectWrap}>
                          <TCSelect
                            options={optionsFor(selections[a._id])}
                            value={selections[a._id] || null}
                            onChange={(opt) => handleAssign(a._id, opt)}
                            placeholder="Select participant…"
                            isClearable
                            disabled={disabled}
                          />
                        </div>
                      </div>

                      <div style={S.slotRow}>
                        <span style={S.seedTag}>{b.seedNumber}</span>
                        <div style={S.selectWrap}>
                          <TCSelect
                            options={optionsFor(selections[b._id])}
                            value={selections[b._id] || null}
                            onChange={(opt) => handleAssign(b._id, opt)}
                            placeholder="Select participant…"
                            isClearable
                            disabled={disabled}
                          />
                        </div>
                      </div>
                    </div>

                    {/* connector elbow to next round */}
                    {/* {totalRounds > 1 && (
                      <div style={S.connector}>
                        <div style={S.connectorArm} />
                        <div style={S.connectorArm} />
                      </div>
                    )} */}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Later rounds: placeholders only */}
          {/* {Array.from({ length: totalRounds - 1 }, (_, r) => {
            const ri = r + 1;
            const matches = size / Math.pow(2, ri + 1);
            return (
              <div key={`round-${ri}`} style={S.roundCol}>
                <div style={S.roundHeader}>
                  {ri === totalRounds - 1 ? "Final" : `Round ${ri + 1}`}
                </div>
                <div style={{ ...S.roundStack, rowGap: 32 * ri }}>
                  {Array.from({ length: matches }, (_, mi) => (
                    <div key={`r${ri}-m${mi}`} style={S.matchBox}>
                      <div style={S.matchInner}>
                        <div style={S.placeholder}>
                          Winner of R{ri}-M{mi * 2 + 1}
                        </div>
                        <div style={S.placeholder}>
                          Winner of R{ri}-M{mi * 2 + 2}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })} */}
        </div>
      </div>

      {/* <Grid container spacing={1} mt={1}>
        <Grid item>
          <div style={S.legendBadge} />
        </Grid>
        <Grid item>
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            Edit participants per <b>slotId</b> in Round 1. Later rounds are placeholders—fill via your advancement logic.
          </div>
        </Grid>
      </Grid> */}
    </div>
  );
}

/* ---------------- styles ---------------- */
const styles = {
  wrap: { display: "flex", flexDirection: "column", gap: 8 },
  toolbar: { display: "flex", alignItems: "baseline", gap: 12, padding: "6px 2px" },
  subtitle: { fontSize: 12, color: "#6B7280" },
  scroller: {
    overflow: "auto",
    padding: 8,
    border: "1px solid #E5E7EB",
    borderRadius: 8,
    background: "#fff",
  },
  grid: { display: "grid", gap: 24, alignItems: "start" },
  roundCol: { minWidth: 300 },
  roundHeader: { fontWeight: 600, fontSize: 14, color: "#111827", marginBottom: 8 },
  roundStack: { display: "grid" },
  matchBox: { position: "relative", paddingRight: 28 },
  matchInner: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    background: "#F9FAFB",
    padding: 8,
    display: "grid",
    gap: 6,
  },
  slotRow: { display: "grid", gridTemplateColumns: "36px 1fr", alignItems: "center", gap: 8 },
  seedTag: {
    width: 32,
    height: 28,
    lineHeight: "28px",
    textAlign: "center",
    borderRadius: 6,
    background: "#EEF2FF",
    color: "#3730A3",
    fontWeight: 600,
    fontSize: 13,
  },
  selectWrap: { width: "100%" },
  placeholder: {
    height: 36,
    borderRadius: 6,
    border: "1px dashed #E5E7EB",
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
    padding: "0 10px",
    fontSize: 13,
    color: "#6B7280",
  },
  connector: { position: "absolute", top: "50%", right: 0, width: 28, height: 0, transform: "translateY(-50%)" },
  connectorArm: { position: "absolute", right: 0, width: 28, height: 1, background: "#E5E7EB", top: -12, boxShadow: "0 24px 0 0 #E5E7EB" },
  legendBadge: { width: 12, height: 12, borderRadius: 3, background: "#EEF2FF", border: "1px solid #CBD5E1" },
};
